import React, { useMemo } from "react";
import { Col, Row } from "reactstrap";
import { Route, Switch } from "react-router-dom";
import { Component as Form } from "@repoflow/form-schema";
import { useSectionContext } from "@repoflow/components/section";
import { useEditor } from "@repoflow/components/editor";
import { uid } from "@repoflow/components/uid";
import * as widgets from "widgets/register";

import { useEditorPage } from "../context";
import { TYPE_FIELD } from "../element-type";
import { sectionCascade, sectionOptions, withSection } from "../sections";
import { TranslationMark, progressLabel, sharedNote } from "../translation";
import * as ElementScreen from "../element";

// Open this element on its own screen. The pencil in gen-2, and the reason the
// element route exists: a `code` element has three code fields, a type
// declaration and the values filled into it, which is not something a modal in
// a list row can hold.
const EditElement = ({ index }) => {
  const { openElement } = useEditorPage();

  return (
    <button
      className="btn btn-link"
      title="Edit this element"
      onClick={(e) => {
        e.preventDefault();
        openElement(index);
      }}
    >
      <i className="fa fa-edit"></i>
    </button>
  );
};

// Find this element in the preview: highlight it, dim everything else, scroll
// to it. Clicking the crosshair of the element already selected clears it.
const GoToElement = ({ index }) => {
  const { select, selected } = useEditor();

  return (
    <button
      className={`btn btn-link${selected === index ? " text-warning" : ""}`}
      title="Show this element in the preview"
      onClick={(e) => {
        e.preventDefault();
        select(index);
      }}
    >
      <i className="fa fa-crosshairs"></i>
    </button>
  );
};

// The Elements tab's form config, as a function of the three things about it
// that vary.
//
// Module scope and exported so that `elements.test.js` drives THIS object
// rather than a copy of it: the cascade below is a form-schema callback three
// components deep in a modal, and a test that rebuilt the config by hand would
// pass while the screen was wired to something else.
export const elementsConfig = ({ sections, options, applySection, openElement }) => ({
  "/Public/Page/Update": {
    fields: [
      {
        property: "elements",
        // The label stays. It is the accessible handle on the list widget:
        // its "+" is named "Add to <label>", and the e2e suite reaches the
        // rows and the pencil through the <dt> beside them.
        layout: "wide",
        // The row's own pencil opens the modal; this one opens the element
        // route, which is the only place a `code` element can actually be
        // edited. Two edit controls in one row is one too many.
        editable: false,
        items: {
          controls: [
            { component: EditElement, mode: ["edit"] },
            { component: GoToElement, mode: ["edit"] },
            // Reads the row and writes it back through the widget's own
            // `setValue`, which is what makes the click survive the list's next
            // render — see ../translation. It draws nothing on an element
            // nothing has propagated into, which is every element of a page
            // nobody has translated.
            { component: TranslationMark, mode: ["edit"] },
          ],
        },
        // What happens the moment an element is added: it inherits whatever
        // section it was pointed at, and then its own screen opens.
        //
        // Adding goes straight to that screen because that is what gen-2 did,
        // and it also stepped around P137: opening the element unmounts the
        // list widget, so the stale `editing` index that stopped the "+"
        // reopening never survived. P137 is fixed as of
        // @repoflow/form-schema 1.3.1, so this is gen-2 parity now rather
        // than a workaround — the navigation is worth keeping on its own
        // merits, but it is no longer load-bearing.
        //
        // The inheritance is HERE, and ../sections says why it cannot be a
        // cascade off the picker: this is the first moment at which nothing
        // else is writing the element. The write goes through `parent`, which
        // is the page's own value object — the same one the list field
        // belongs to — and it happens before the navigation so that the
        // element's screen opens on the finished element.
        //
        // The whole operation log is searched rather than only its last
        // entry, which is what gen-2 read. The log is assembled by four
        // nested setValue wrappers, each prepending its own entry, and
        // which one ends up last is a property of that nesting rather than
        // of what happened — so reading the tail is right until a widget
        // gains a wrapper, and then it silently stops navigating.
        onChange: ({ value, operations, parent }) => {
          const added = (operations || []).find(({ name }) => name === "list.add");

          if (!added) {
            return;
          }

          const elements = value || [];
          const element = elements[added.index];
          const inherited = withSection(element, sections);

          if (inherited !== element) {
            const modified = [...elements];
            modified[added.index] = inherited;
            parent.setValues({ elements: modified });
          }

          openElement(added.index);
        },
      },
    ],
  },
  "/Page/Element": {
    // Minted here because an element never round-trips on its own. It reaches
    // the API inside the page's array, so there is no moment at which the
    // server could hand an id back.
    default: () => ({ elementid: uid() }),
    // What it is, what it is called, where it lands — the three columns of the
    // list, and the first three fields the add modal asks for.
    listing: ["type", "name", "tags"],
    // `config` is the fourth, and it is a oneOf resolved by `type`: nothing
    // renders until a type is chosen, and what renders then is the branch that
    // type selected. So choosing "section" reveals the section picker HERE, in
    // the modal, and the element is complete before it is added — rather than
    // being added pointing at nothing and repointed on the screen that opens
    // next.
    //
    // `type` is configured rather than named, and ../element-type says why: the
    // picker needs an option for the "" the field holds when the dialog opens,
    // or it opens showing a type nothing has chosen.
    fields: [
      TYPE_FIELD,
      "name",
      "tags",
      { property: "config", layout: "wide", onChange: applySection },
    ],
  },
  "/Page/Element/Type/Section": {
    fields: [{ property: "sectionId", widget: "select", options }],
  },
  // Deliberately empty, and it has to be present to BE empty: with no entry at
  // all the branch falls back to every property it declares, which would put
  // three code fields, a type declaration and its values in a dialog whose job
  // is to say what the element IS. A `code` element's content is edited on its
  // own screen — that split is gen-2's and it is what the navigation above
  // exists for.
  "/Page/Element/Type/Code": { fields: [] },
  // Empty for the same reason, and here it buys more than tidiness: the one
  // property a paragraph declares is authored by a Quill editor, and its widget
  // mounts a toolbar and a document of its own. A rich-text editor inside the
  // add dialog would be the tallest thing in it, would load a chunk while the
  // dialog is opening, and would be discarded the moment Add navigates to the
  // element's own screen — where the same field is waiting.
  "/Page/Element/Type/Paragraph": { fields: [] },
  // The blog elements are configured on their own screens, where the option
  // lists (templates, collections) live; the dialog asks for the type alone.
  "/Page/Element/Type/BlogPost": { fields: [] },
  "/Page/Element/Type/BlogList": { fields: [] },
  "/Page/Element/Type/BlogPostCollection": { fields: [] },
});

// The page's content, in order.
//
// Order IS the array's order — there is no position column and no reorder
// mutation, the drag handle splices the array and the whole thing goes back on
// save. That is what makes `index: "elementid"` load-bearing on the schema: the
// list widget only offers a drag handle for an array whose rows have an
// identity of their own.
export const Content = () => {
  const { target, schema, state, basepath } = useSectionContext();
  const { sections, languages, openElement } = useEditorPage();
  const { values, setValues } = state;

  // Memoised for the reason every config in this app is: a new identity rebuilds
  // the widget tree beneath the form's SchemaProvider and detaches the control
  // being typed into (P139). The only parts that vary are the picker's options
  // and what it copies, and both vary with the same thing.
  const options = useMemo(() => sectionOptions(sections), [sections]);

  // Picking a section fills the element in while the dialog is still open —
  // and it has to, because Add stays disabled until the element has a name.
  // ../sections has the mechanism, and why the copy is ALSO made when the
  // element is added rather than trusted to this alone.
  const applySection = useMemo(() => sectionCascade(sections), [sections]);

  const config = useMemo(
    () => elementsConfig({ sections, options, applySection, openElement }),
    [sections, options, applySection, openElement]
  );

  // An element is a route, not state — `/edit/element/:index`, which is how
  // gen-2 addressed one and what P144 cost this screen while it was open. The
  // Switch sits INSIDE the Elements tab rather than above the tab strip because
  // the strip is where Save lives, and Save has to stay reachable while you are
  // editing an element: three tabs and this screen all write one value object.
  //
  // `:index(\\d+)` rather than `:index`. The screen reads `elements[index]`, so
  // a path segment that is not a number would render "No element #NaN"; not
  // matching at all falls through to the list below, which is what a mistyped
  // URL should do.
  return (
    <Switch>
      <Route
        path={`${basepath}/element/:index(\\d+)`}
        component={ElementScreen.Content}
      />
      <Route path={`${basepath}`}>
        {/* What a change here is about to do, said before it does it.
            Both halves are drawn OUTSIDE the form and not as fields on it: the
            note is about the list rather than about any row of it, and the
            count is derived from the value rather than stored anywhere. Neither
            is in the memoised config, so neither can rebuild the widget tree
            under an open modal (P139). */}
        <TranslationNote languages={languages} page={values} />
        <Row>
          <Col>
            <Form
              schema={schema}
              target={target}
              widgets={widgets}
              values={values}
              onChange={setValues}
              config={config}
            />
          </Col>
        </Row>
      </Route>
    </Switch>
  );
};

// The two sentences the Elements tab carries when its page is one of several
// languages: what a structural change reaches, and how much of this page is
// still in the language it was translated from.
//
// Exported so `index.test.js` drives THIS component rather than a copy of the
// wording, the same reason `elementsConfig` above is exported.
export const TranslationNote = ({ languages, page }) => {
  const note = sharedNote(languages);

  if (!note) {
    return null;
  }

  const progress = progressLabel(page);

  return (
    <Row>
      <Col>
        <div className="alert alert-info d-flex justify-content-between align-items-center">
          <span>{note}</span>
          {progress ? (
            <span className="badge badge-light ml-3 text-nowrap">{progress}</span>
          ) : null}
        </div>
      </Col>
    </Row>
  );
};
